import { useToast } from '@chakra-ui/react'
import { useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  bulkSetItemCompliant,
  bulkSetItemManualNtdReminder,
  bulkSetItemMemberReview,
  bulkSetItemNonInfringing,
  bulkSetItemRemoved,
  setItemAnnotatedInfringing,
  setItemFlagged,
  setItemIdle,
  setItemManualNtd,
  setItemMemberCompliant,
  setItemMemberInfringing,
  setItemMemberInfringingNoNtd,
  setItemMemberUncertain,
  setItemRelisted,
} from '../api/react/items/Items'
import { useBulkActionsStore } from '../stores/bulkActionsStore'
import handleErrorToast from '../components/common/toast/handleErrorToast'
import handleSuccessToast from '../components/common/toast/handleSuccessToast'

/**
 * Global hook that auto-resumes interrupted bulk jobs
 * This runs at the app level so jobs resume regardless of which page you're on
 */
export const useBulkJobAutoResume = () => {
  // Use targeted selectors to avoid subscribing to the entire bulkActions array
  const updateBulkActionJob = useBulkActionsStore((state) => state.updateBulkActionJob)
  const setAutoResumeInProgress = useBulkActionsStore((state) => state.setAutoResumeInProgress)
  const toast = useToast()

  // Set up all mutation hooks
  const bulkSetItemAnnotatedInfringingMutation = useMutation({ mutationFn: setItemAnnotatedInfringing })
  const bulkSetItemMemberReviewMutation = useMutation({ mutationFn: bulkSetItemMemberReview })
  const bulkSetItemCompliantMutation = useMutation({ mutationFn: bulkSetItemCompliant })
  const bulkSetItemRemovedMutation = useMutation({ mutationFn: bulkSetItemRemoved })
  const bulkSetItemRelistedMutation = useMutation({ mutationFn: setItemRelisted })
  const bulkSetItemManualNtdReminderMutation = useMutation({ mutationFn: bulkSetItemManualNtdReminder })
  const bulkSetItemManualNtdMutation = useMutation({ mutationFn: setItemManualNtd })
  const setItemFlaggedMutation = useMutation({ mutationFn: setItemFlagged })
  const bulkSetItemsNonInfringingMutation = useMutation({ mutationFn: bulkSetItemNonInfringing })
  const bulkSetItemIdleMutation = useMutation({ mutationFn: setItemIdle })
  const setItemMemberCompliantMutation = useMutation({ mutationFn: setItemMemberCompliant })
  const setItemMemberInfringingMutation = useMutation({ mutationFn: setItemMemberInfringing })
  const setItemMemberInfringingNoNtdMutation = useMutation({ mutationFn: setItemMemberInfringingNoNtd })
  const setItemMemberUncertainMutation = useMutation({ mutationFn: setItemMemberUncertain })

  // Helper function to process items in chunks
  const chunkArray = <T>(array: T[], size: number): T[][] => {
    const chunks: T[][] = []
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size))
    }
    return chunks
  }

  // Main processing function that handles resume logic
  const processJob = async <T extends { item_ids: string[] }>(mutationFn: (data: T) => Promise<unknown>, job: any) => {
    const BATCH_SIZE = 5

    const alreadyProcessedIds = job.processedItemIds || []
    const itemsToProcess = job.itemIds.filter((id: string) => !alreadyProcessedIds.includes(id))
    const chunks = chunkArray(itemsToProcess, BATCH_SIZE)

    if (import.meta.env.DEV) console.log(
      `[Auto-Resume] Processing job ${job.id}: ${itemsToProcess.length} items remaining of ${job.itemIds.length} total`
    )

    // Mark job as running
    updateBulkActionJob(job.id, {
      status: 'running',
      processed: alreadyProcessedIds.length,
    })

    try {
      let processedCount = alreadyProcessedIds.length
      const newlyProcessedIds = [...alreadyProcessedIds]

      for (const chunk of chunks) {
        await mutationFn({ ...job.mutationData, item_ids: chunk } as T)
        processedCount += chunk.length
        newlyProcessedIds.push(...chunk)

        // Update progress after each chunk
        updateBulkActionJob(job.id, {
          processed: processedCount,
          status: 'running',
          processedItemIds: newlyProcessedIds,
        })
      }

      // Mark as completed
      updateBulkActionJob(job.id, {
        status: 'completed',
        processed: job.itemIds.length,
        processedItemIds: newlyProcessedIds,
      })

      if (import.meta.env.DEV) console.log(`[Auto-Resume] Job ${job.id} completed successfully`)
    } catch (error) {
      console.error(`[Auto-Resume] Job ${job.id} failed:`, error)
      updateBulkActionJob(job.id, {
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error',
      })
      throw error
    }
  }

  useEffect(() => {
    const resumeInterruptedJobs = async () => {
      if (import.meta.env.DEV) console.log('[Auto-Resume] Checking for interrupted jobs...')

      // Get current bulkActions directly from store to avoid stale closures
      const currentBulkActions = useBulkActionsStore.getState().bulkActions
      const resumableJobs = currentBulkActions.filter(
        (job) =>
          (job.status === 'running' || job.status === 'queued') &&
          job.itemIds &&
          job.itemIds.length > 0 &&
          job.mutationData !== undefined
      )

      if (resumableJobs.length === 0) {
        if (import.meta.env.DEV) console.log('[Auto-Resume] No jobs to resume')
        return
      }

      // Set flag to prevent cleanup from removing jobs
      setAutoResumeInProgress(true)
      if (import.meta.env.DEV) console.log(`[Auto-Resume] Starting resume for ${resumableJobs.length} jobs`)

      handleSuccessToast(
        toast,
        'Resuming tasks',
        `Automatically resuming ${resumableJobs.length} interrupted task${resumableJobs.length > 1 ? 's' : ''}...`
      )

      // Start all jobs in parallel
      const jobPromises = resumableJobs.map(async (job) => {
        if (import.meta.env.DEV) console.log(`[Auto-Resume] Processing job ${job.id} (${job.type})`)

        // Map job type to mutation function
        const mutationMap: Record<string, any> = {
          annotate_infringing: bulkSetItemAnnotatedInfringingMutation.mutateAsync,
          deannotate_infringing: bulkSetItemAnnotatedInfringingMutation.mutateAsync,
          member_review: bulkSetItemMemberReviewMutation.mutateAsync,
          mark_compliant: bulkSetItemCompliantMutation.mutateAsync,
          mark_removed: bulkSetItemRemovedMutation.mutateAsync,
          mark_relisted: bulkSetItemRelistedMutation.mutateAsync,
          manual_ntd_reminder: bulkSetItemManualNtdReminderMutation.mutateAsync,
          manual_ntd: bulkSetItemManualNtdMutation.mutateAsync,
          flag_for_review: setItemFlaggedMutation.mutateAsync,
          mark_non_infringing: bulkSetItemsNonInfringingMutation.mutateAsync,
          reset_idle: bulkSetItemIdleMutation.mutateAsync,
          hide_items: bulkSetItemsNonInfringingMutation.mutateAsync,
          member_compliant: setItemMemberCompliantMutation.mutateAsync,
          member_infringing: setItemMemberInfringingMutation.mutateAsync,
          member_infringing_no_ntd: setItemMemberInfringingNoNtdMutation.mutateAsync,
          member_uncertain: setItemMemberUncertainMutation.mutateAsync,
        }

        const mutationFn = mutationMap[job.type]
        if (mutationFn && job.itemIds) {
          try {
            await processJob(mutationFn, job)
            if (import.meta.env.DEV) console.log(`[Auto-Resume] Successfully resumed job ${job.id}`)
          } catch (error) {
            console.error(`[Auto-Resume] Failed to resume job ${job.id}:`, error)
            handleErrorToast(toast, 'Resume failed', `Failed to resume task: ${job.type}`)
          }
        } else {
          console.warn(`[Auto-Resume] No mutation function found for job type: ${job.type}`)
        }
      })

      // Wait for all jobs to start, then clear the flag
      await Promise.allSettled(jobPromises)
      setAutoResumeInProgress(false)
    }

    // Run auto-resume after a short delay to let the app initialize
    const timer = setTimeout(resumeInterruptedJobs, 1500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount
}
